import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '@store/store';
import type {
  DriverChatConnection,
  DriverChatSeed,
} from '@component/DriverChat/types';

export type SegmentDetails = Pick<
  DriverChatSeed,
  | 'memberFirstName'
  | 'memberLastName'
  | 'memberImage'
  | 'confirmationCode'
  | 'segmentNumber'
>;

const pickSegmentDetails = (seed: DriverChatSeed): SegmentDetails => ({
  memberFirstName: seed.memberFirstName,
  memberLastName: seed.memberLastName,
  memberImage: seed.memberImage,
  confirmationCode: seed.confirmationCode,
  segmentNumber: seed.segmentNumber,
});

// Drafts live in memory only: chat text is never written to browser storage.
type DriverChatState = {
  isPanelOpen: boolean;
  panelConversationId: number | null;
  /** Always carries a conversationId: the entry button opens the conversation first. */
  panelSeed: DriverChatSeed | null;
  /** The conversation request for panelSeed failed; the popup shows the error. */
  panelSeedFailed: boolean;
  drafts: Record<number, string>;
  /** Driver phone/photo captured when a chat was started; the API does not return them. */
  drivers: Record<number, { phone?: string; image?: string }>;
  /** Segments on screen whose driver has accepted: lets the launcher appear without a live event. */
  trackedSegments: Record<number, DriverChatSeed>;
  /** Member and ride reference of every segment seen this session; the conversations API does not return them. */
  segmentDetails: Record<number, SegmentDetails>;
  /** Once a driver has accepted a ride, the launcher stays on every screen. */
  isLauncherLatched: boolean;
  connection: DriverChatConnection;
};

const initialState: DriverChatState = {
  isPanelOpen: false,
  panelConversationId: null,
  panelSeed: null,
  panelSeedFailed: false,
  drafts: {},
  drivers: {},
  trackedSegments: {},
  segmentDetails: {},
  isLauncherLatched: false,
  connection: 'connecting',
};

// A newer value replaces the old one; a missing one never erases what we know.
const rememberDetails = (state: DriverChatState, seed: DriverChatSeed) => {
  const next = Object.fromEntries(
    Object.entries(pickSegmentDetails(seed)).filter(([, v]) => !!v)
  );
  state.segmentDetails[seed.segmentId] = {
    ...state.segmentDetails[seed.segmentId],
    ...next,
  };
};

const driverChatSlice = createSlice({
  name: 'driverChat',
  initialState,
  reducers: {
    openPanelWithSeed(state, action: PayloadAction<DriverChatSeed>) {
      state.panelSeed = action.payload;
      state.panelSeedFailed = false;
      state.isPanelOpen = true;
      state.panelConversationId = action.payload.conversationId ?? null;
      rememberDetails(state, action.payload);
    },
    failPanelSeed(state) {
      state.panelSeedFailed = true;
    },
    toggleDriverChatPanel(state) {
      state.isPanelOpen = !state.isPanelOpen;
      if (!state.isPanelOpen) {
        state.panelSeed = null;
        state.panelSeedFailed = false;
      }
    },
    closeDriverChatPanel(state) {
      state.isPanelOpen = false;
      state.panelSeed = null;
      state.panelSeedFailed = false;
    },
    openPanelConversation(state, action: PayloadAction<number | null>) {
      state.isPanelOpen = true;
      state.panelConversationId = action.payload;
      state.panelSeed = null;
      state.panelSeedFailed = false;
    },
    setDriverChatDraft(
      state,
      action: PayloadAction<{ conversationId: number; text: string }>
    ) {
      const { conversationId, text } = action.payload;
      if (text) state.drafts[conversationId] = text;
      else delete state.drafts[conversationId];
    },
    rememberChatDriver(
      state,
      action: PayloadAction<{
        segmentId: number;
        phone?: string;
        image?: string;
      }>
    ) {
      const { segmentId, phone, image } = action.payload;
      // A newer value replaces the old one; an absent one never erases what we know.
      state.drivers[segmentId] = {
        ...state.drivers[segmentId],
        ...(phone ? { phone } : {}),
        ...(image ? { image } : {}),
      };
    },
    trackChatSegment(state, action: PayloadAction<DriverChatSeed>) {
      state.trackedSegments[action.payload.segmentId] = action.payload;
      rememberDetails(state, action.payload);
    },
    rememberChatSegmentDetails(
      state,
      action: PayloadAction<{ segmentId: number; details: SegmentDetails }>
    ) {
      const { segmentId, details } = action.payload;
      const known = Object.fromEntries(
        Object.entries(details).filter(([, v]) => !!v)
      );
      state.segmentDetails[segmentId] = {
        ...known,
        ...state.segmentDetails[segmentId],
      };
    },
    untrackChatSegment(state, action: PayloadAction<number>) {
      delete state.trackedSegments[action.payload];
    },
    latchDriverChatLauncher(state) {
      state.isLauncherLatched = true;
    },
    setDriverChatConnection(
      state,
      action: PayloadAction<DriverChatConnection>
    ) {
      state.connection = action.payload;
    },
    resetDriverChat() {
      return initialState;
    },
  },
});

export const {
  openPanelWithSeed,
  failPanelSeed,
  toggleDriverChatPanel,
  closeDriverChatPanel,
  openPanelConversation,
  setDriverChatDraft,
  rememberChatDriver,
  trackChatSegment,
  untrackChatSegment,
  rememberChatSegmentDetails,
  latchDriverChatLauncher,
  setDriverChatConnection,
  resetDriverChat,
} = driverChatSlice.actions;

export const selectDriverChat = (state: RootState) => state.driverChat;

/** Conversation ids whose thread is currently on screen in the popup. */
export const selectVisibleConversationIds = (state: RootState) => {
  const { isPanelOpen, panelConversationId } = state.driverChat;
  return isPanelOpen && panelConversationId != null
    ? [panelConversationId]
    : [];
};

export const driverChatReducer = driverChatSlice.reducer;
